if (!customElements.get('presale-countdown')) {
  class PresaleCountdown extends HTMLElement {
    connectedCallback() {
      this.daysEl = this.querySelector('.js-pa-days');
      this.hoursEl = this.querySelector('.js-pa-hours');
      this.minsEl = this.querySelector('.js-pa-mins');
      this.secsEl = this.querySelector('.js-pa-secs');
      this.liveEl = this.querySelector('[data-pa-live]');

      this.liveTemplate =
        this.dataset.liveTemplate ||
        '{{ days }} days, {{ hours }} hours, {{ minutes }} minutes remaining';
      this.endedMessage = this.dataset.endedMessage || 'Countdown complete.';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.lastAnnouncedDay = null;
      this.lastAnnouncedHour = null;
      this.hasAnnouncedEnd = false;

      this.targetTime = this.parseTargetTime(
        this.dataset.endDate || '',
        this.dataset.endTime || '00:00',
        this.dataset.timezone || 'America/New_York'
      );

      const earlyAccessHours = parseFloat(this.dataset.earlyAccessHours || '0', 10);
      if (
        this.targetTime &&
        !Number.isNaN(this.targetTime) &&
        Number.isFinite(earlyAccessHours) &&
        earlyAccessHours > 0
      ) {
        this.targetTime -= earlyAccessHours * 60 * 60 * 1000;
      }

      if (!this.targetTime || Number.isNaN(this.targetTime)) {
        console.warn('PresaleCountdown: Invalid target date');
        return;
      }

      this.update();
      const intervalMs = this.reducedMotion ? 60000 : 1000;
      this.interval = window.setInterval(() => this.update(), intervalMs);
    }

    disconnectedCallback() {
      if (this.interval) {
        window.clearInterval(this.interval);
      }
    }

    parseTargetTime(rawDate, rawTime, timeZone) {
      const zonedTarget = this.zonedTimeToUtc(rawDate, rawTime, timeZone);
      if (zonedTarget) {
        return zonedTarget;
      }

      return this.parseTargetDate(rawDate, rawTime);
    }

    zonedTimeToUtc(rawDate, rawTime, timeZone) {
      const dateParts = rawDate.split('-');
      if (dateParts.length !== 3) {
        return null;
      }

      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10);
      const day = parseInt(dateParts[2], 10);

      const timeParts = rawTime.split(':');
      const hours = parseInt(timeParts[0], 10) || 0;
      const minutes = parseInt(timeParts[1], 10) || 0;

      if (
        Number.isNaN(year) ||
        Number.isNaN(month) ||
        Number.isNaN(day) ||
        Number.isNaN(hours) ||
        Number.isNaN(minutes)
      ) {
        return null;
      }

      let utcGuess = Date.UTC(year, month - 1, day, hours, minutes, 0);

      try {
        for (let i = 0; i < 3; i += 1) {
          const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23'
          });

          const parts = Object.fromEntries(
            formatter.formatToParts(new Date(utcGuess)).map(({ type, value }) => [type, value])
          );

          const zonedAsUtc = Date.UTC(
            Number(parts.year),
            Number(parts.month) - 1,
            Number(parts.day),
            Number(parts.hour),
            Number(parts.minute),
            Number(parts.second)
          );

          const desiredUtc = Date.UTC(year, month - 1, day, hours, minutes, 0);
          const diff = desiredUtc - zonedAsUtc;
          utcGuess += diff;
        }
      } catch (error) {
        console.warn('PresaleCountdown: Invalid timezone', timeZone, error);
        return null;
      }

      return utcGuess;
    }

    parseTargetDate(rawDate, rawTime) {
      const dateParts = rawDate.split('-');
      if (dateParts.length !== 3) {
        return null;
      }

      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1;
      const day = parseInt(dateParts[2], 10);

      const timeParts = rawTime.split(':');
      const hours = parseInt(timeParts[0], 10) || 0;
      const minutes = parseInt(timeParts[1], 10) || 0;

      return new Date(year, month, day, hours, minutes, 0, 0).getTime();
    }

    update() {
      const diff = Math.max(this.targetTime - Date.now(), 0);

      const days = Math.floor(diff / (24 * 60 * 60 * 1000));
      const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
      const mins = Math.floor((diff % (60 * 60 * 1000)) / (60 * 1000));
      const secs = Math.floor((diff % (60 * 1000)) / 1000);

      this.setDisplay(days, hours, mins, secs);
      this.updateLiveRegion(days, hours, mins, diff);

      if (diff <= 0 && this.interval) {
        window.clearInterval(this.interval);
        this.interval = null;
      }
    }

    updateLiveRegion(days, hours, mins, diff) {
      if (!this.liveEl) {
        return;
      }

      if (diff <= 0) {
        if (!this.hasAnnouncedEnd) {
          this.liveEl.textContent = this.endedMessage;
          this.hasAnnouncedEnd = true;
        }
        return;
      }

      if (days !== this.lastAnnouncedDay || hours !== this.lastAnnouncedHour) {
        this.liveEl.textContent = this.formatLiveMessage(days, hours, mins);
        this.lastAnnouncedDay = days;
        this.lastAnnouncedHour = hours;
      }
    }

    formatLiveMessage(days, hours, mins) {
      return this.liveTemplate
        .replace(/\{\{\s*days\s*\}\}/g, String(days))
        .replace(/\{\{\s*hours\s*\}\}/g, String(hours))
        .replace(/\{\{\s*minutes\s*\}\}/g, String(mins));
    }

    setDisplay(days, hours, mins, secs) {
      if (this.daysEl) {
        this.daysEl.textContent = String(days).padStart(2, '0');
      }
      if (this.hoursEl) {
        this.hoursEl.textContent = String(hours).padStart(2, '0');
      }
      if (this.minsEl) {
        this.minsEl.textContent = String(mins).padStart(2, '0');
      }
      if (this.secsEl) {
        this.secsEl.textContent = String(secs).padStart(2, '0');
      }
    }
  }

  customElements.define('presale-countdown', PresaleCountdown);
}

(function () {
  var KLAVIYO_REVISION = '2025-07-15';
  var CONFIRM_PARAM = 'presale';
  var CONFIRM_VALUE = 'confirmed';

  function getQueryParam(name) {
    return new URLSearchParams(window.location.search).get(name);
  }

  function showPresaleSuccess(root) {
    if (!root) return;
    var capture = root.querySelector('[data-presale-capture]');
    var success = root.querySelector('[data-presale-success]');
    if (!success) return;

    if (capture) capture.hidden = true;
    success.hidden = false;

    window.requestAnimationFrame(function () {
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (typeof success.focus === 'function') {
        success.focus({ preventScroll: true });
      }
    });
  }

  function activatePresaleSuccessFromUrl() {
    if (getQueryParam(CONFIRM_PARAM) === CONFIRM_VALUE) {
      document.querySelectorAll('[data-presale-klaviyo]').forEach(function (form) {
        showPresaleSuccess(form);
      });
      return;
    }

    if (getQueryParam('customer_posted') !== 'true') return;

    var sectionId = getQueryParam('presale_section');
    if (!sectionId) return;

    var section =
      document.getElementById('presale-announcement-' + sectionId) ||
      document.getElementById('product-presale-' + sectionId);
    showPresaleSuccess(section);
  }

  function setFormError(form, message) {
    var errorEl = form.querySelector('[data-presale-error]');
    if (!errorEl) return;
    errorEl.hidden = !message;
    errorEl.textContent = message || '';
  }

  function buildConfirmationUrl() {
    var url = new URL(window.location.href);
    url.searchParams.set(CONFIRM_PARAM, CONFIRM_VALUE);
    return url.toString();
  }

  function subscribeToKlaviyo(form, email) {
    var companyId = form.getAttribute('data-presale-company-id') || '';
    var listId = form.getAttribute('data-presale-list-id') || '';
    var customSource = form.getAttribute('data-presale-custom-source') || 'Brass Switch Pre-Sale';
    var signupSource = form.getAttribute('data-presale-signup-source') || '';
    var property = form.getAttribute('data-presale-property') || '';

    var properties = {};
    if (signupSource) properties.presale_signup_source = signupSource;
    if (property) properties[property] = true;

    var profileAttributes = {
      email: email,
      properties: properties,
      subscriptions: {
        email: {
          marketing: {
            consent: 'SUBSCRIBED'
          }
        }
      }
    };

    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: {
        revision: KLAVIYO_REVISION,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: customSource,
            profile: {
              data: {
                type: 'profile',
                attributes: profileAttributes
              }
            }
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: listId
              }
            }
          }
        }
      })
    });
  }

  function onPresaleSubmit(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('[data-presale-klaviyo]')) return;

    event.preventDefault();
    event.stopPropagation();

    var emailInput = form.querySelector('input[type="email"], input[name="contact[email]"]');
    var email = emailInput && emailInput.value ? emailInput.value.trim() : '';
    if (!email) {
      setFormError(form, 'Please enter a valid email.');
      if (emailInput) emailInput.focus();
      return;
    }

    var submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
    }
    setFormError(form, '');

    subscribeToKlaviyo(form, email)
      .then(function (response) {
        if (!response.ok && response.status !== 202) {
          throw new Error('Klaviyo subscription failed');
        }
        window.location.assign(buildConfirmationUrl());
      })
      .catch(function () {
        setFormError(form, 'Something went wrong. Please try again.');
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.removeAttribute('aria-busy');
        }
      });
  }

  document.addEventListener('submit', onPresaleSubmit, true);

  function initPresaleFormSuccess() {
    activatePresaleSuccessFromUrl();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPresaleFormSuccess);
  } else {
    initPresaleFormSuccess();
  }
})();
